import { ButtonBase, makeStyles } from '@material-ui/core';
import { addYears, format, isSameMonth, isThisMonth, isValid, startOfYear } from 'date-fns';
import PropTypes from 'prop-types';
import React from 'react';
import DayPicker from 'react-day-picker';
import { ArrowV } from '~svg-components';

const useStyles = makeStyles((theme) => ({
  '@global': {
    '.DayPicker': {
      display: 'inline-flex',
      backgroundColor: theme.palette.common.white,
      borderRadius: theme.borderRadius.small,
      boxShadow: theme.shadow.componentDark,
      border: `solid 1px ${theme.colors.greyE8}`,
      zIndex: 2,
      height: 304,

      '&-wrapper': { boxSizing: 'border-box', width: 264, padding: 20 },

      '&-Weekdays': { display: 'none' },

      '&-Body': { display: 'none' },

      '&-NavBar': {
        marginBottom: 10,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '& > span': {
          fontSize: theme.typography.pxToRem(14),
          fontWeight: 'bold',
          lineHeight: 1.43,
          color: theme.colors.dark,
        },
      },

      '&-NavButton': {
        width: 32,
        height: 32,
        '& svg': { width: 8, fill: theme.colors.grey66 },
        '&:hover': { '& svg': { fill: theme.colors.brandAction } },
        '&--prev': { '& svg': { transform: 'rotate(180deg)' } },
      },

      '&-Months': { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' },
    },
  },

  monthButton: {
    width: 64,
    height: 40,
    color: theme.colors.dark,
    borderRadius: theme.borderRadius.small,

    '&:not($active):hover': {
      backgroundColor: theme.colors.greyFa,
    },
  },

  thisMonth: {
    '&:after': {
      content: "''",
      position: 'absolute',
      bottom: 4,
      width: 4,
      height: 4,
      borderRadius: '50%',
      backgroundColor: theme.colors.brandIcon,
      left: '50%',
      transform: 'translateX(-50%)',
    },
  },

  active: {
    backgroundColor: theme.colors.grey66,
    color: theme.palette.common.white,
    fontWeight: 'bold',
  },
}));

const MonthPicker = React.forwardRef((props, ref) => {
  const { selectedDays, onMonthChange } = props;
  const [selectedMonth] = selectedDays;
  const initialMonth = isValid(selectedMonth)
    ? startOfYear(selectedMonth)
    : startOfYear(new Date());

  const classes = useStyles();
  return (
    <DayPicker
      ref={ref}
      numberOfMonths={12}
      weekdayElement={() => null}
      navbarElement={(navbarProps) => {
        const { className, month } = navbarProps;
        return (
          <div className={className}>
            <ButtonBase
              className="DayPicker-NavButton DayPicker-NavButton--prev"
              disableRipple
              onClick={() => {
                if (onMonthChange) onMonthChange(addYears(month, -1));
              }}
            >
              <ArrowV />
            </ButtonBase>
            <span>{month.getFullYear()}</span>
            <ButtonBase
              className="DayPicker-NavButton DayPicker-NavButton--next"
              disableRipple
              onClick={() => {
                if (onMonthChange) onMonthChange(addYears(month, 1));
              }}
            >
              <ArrowV />
            </ButtonBase>
          </div>
        );
      }}
      captionElement={(captionProps) => {
        const { date, onClick } = captionProps;
        return (
          <ButtonBase
            disableRipple
            className={`${classes.monthButton} ${
              isSameMonth(selectedMonth, date) ? classes.active : ''
            } ${isThisMonth(date) ? classes.thisMonth : ''}`}
            onClick={onClick}
          >
            {format(date, 'MMM')}
          </ButtonBase>
        );
      }}
      initialMonth={initialMonth}
      {...props}
    />
  );
});

MonthPicker.propTypes = {
  selectedDays: PropTypes.array,
  onMonthChange: PropTypes.func,
};

MonthPicker.defaultProps = {
  selectedDays: [],
  onMonthChange: undefined,
};

export default MonthPicker;
